import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import CustomTextField from "../../../components/common/Textfield";

interface BookingLink {
  id?: string;
  title: string;
  url: string;
}

interface PreviousBookingLinksSectionProps {
  links: BookingLink[];
  onChange: (links: BookingLink[]) => void;
  showLinks?: boolean;
  onShowLinksChange?: (show: boolean) => void;
}

export default function PreviousBookingLinksSection({
  links,
  onChange,
  showLinks = false,
  onShowLinksChange,
}: PreviousBookingLinksSectionProps) {
  const handleAddLink = () => {
    const newLink: BookingLink = {
      id: Date.now().toString(),
      title: "",
      url: "",
    };
    onChange([...links, newLink]);
  };

  const handleUpdateLink = (
    id: string | undefined,
    field: keyof BookingLink,
    value: string
  ) => {
    const updated = links.map((link) =>
      link.id === id ? { ...link, [field]: value } : link
    );
    onChange(updated);
  };

  const handleRemoveLink = (id: string | undefined) => {
    onChange(links.filter((link) => link.id !== id));
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <LinkIcon sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Links to Previous Bookings
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={showLinks}
            onChange={(e) => onShowLinksChange?.(e.target.checked)}
          />
        }
        label="Show links on proposal"
        sx={{ mb: 2 }}
      />

      <Stack spacing={2}>
        {links.map((link) => (
          <Paper
            key={link.id}
            sx={{
              p: 2,
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              boxShadow: 0,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <CustomTextField
                fullWidth
                label="Link Title"
                value={link.title}
                onChange={(e) =>
                  handleUpdateLink(link.id, "title", e.target.value)
                }
                placeholder="e.g., Memorial Service Recording"
                sx={{ mb: 1 }}
              />
              <CustomTextField
                fullWidth
                label="Link URL"
                value={link.url}
                onChange={(e) =>
                  handleUpdateLink(link.id, "url", e.target.value)
                }
                placeholder="https://example.com"
                type="url"
              />
            </Box>
            <IconButton
              color="error"
              onClick={() => handleRemoveLink(link.id)}
              sx={{ mt: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Paper>
        ))}

        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={handleAddLink}
          sx={{ alignSelf: "flex-start" }}
        >
          Add Link
        </Button>
      </Stack>
    </Box>
  );
}
